// @ts-nocheck
// localStorage change events are delivered to every other tab on the same origin.
const SESSION_LOGOUT_STORAGE_KEY = 'vault_session_logout_event';
const SESSION_LOGIN_STORAGE_KEY = 'vault_session_login_event';

/**
 * Write a unique, non-sensitive cross-tab session marker.
 * @param {string} key - localStorage key observed by other tabs.
 * @returns {void}
 */
const notifyTabs = (key) => {
  try {
    localStorage.setItem(key, `${Date.now()}-${Math.random()}`);
  } catch (_error) {
    // Authentication in the initiating tab still proceeds without storage events.
  }
};

/**
 * Subscribe to a session marker emitted by another browser tab.
 * @param {string} key - Marker key to observe.
 * @param {function(): void} callback - Called when another tab writes the marker.
 * @returns {function(): void} Removes the storage listener.
 */
const subscribeToMarker = (key, callback) => {
  const handleStorage = (event) => {
    if (
      event.storageArea === localStorage &&
      event.key === key &&
      event.newValue
    ) {
      callback();
    }
  };

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
};

/**
 * Broadcast a completed local logout to all other tabs on this origin.
 * Call this after authentication storage has been cleared.
 * @returns {void}
 */
export const notifySessionLogout = () => {
  notifyTabs(SESSION_LOGOUT_STORAGE_KEY);
};

/**
 * Broadcast that login data has been completely persisted in this tab.
 * Token refresh does not call this function, so routine rotation does not reload
 * other tabs. The marker contains no token or user information.
 * @returns {void}
 */
export const notifySessionLogin = () => {
  notifyTabs(SESSION_LOGIN_STORAGE_KEY);
};

/**
 * Subscribe to explicit logout notifications emitted by another browser tab.
 * Token removal alone is not considered logout because a missing access token
 * can still be recovered with refresh_token.
 * @param {function(): void} onLogout - Runs once for each remote logout marker.
 * @returns {function(): void} Removes the storage listener.
 */
export const subscribeToSessionLogout = (onLogout) => {
  return subscribeToMarker(SESSION_LOGOUT_STORAGE_KEY, onLogout);
};

/**
 * Subscribe to successful login or session-identity changes in another tab.
 * @param {function(): void} onLogin - Runs after the remote tab persisted login data.
 * @returns {function(): void} Removes the storage listener.
 */
export const subscribeToSessionLogin = (onLogin) => {
  return subscribeToMarker(SESSION_LOGIN_STORAGE_KEY, onLogin);
};
